import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Plus, X } from "lucide-react";
import type {
	ScorecardCategory,
	ScorecardField,
	ScorecardFieldInput,
	ScorecardFieldType,
	ScorecardRatingConfig,
} from "@/types/scorecard";

const FIELD_TYPES: ScorecardFieldType[] = ["rating", "text", "textarea", "number", "boolean", "dropdown"];
const MAX_RATING_SPAN = 10;
const NO_CATEGORY = "none";

interface FieldFormDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	/** Field being edited, or null when creating. */
	field: ScorecardField | null;
	categories: ScorecardCategory[];
	/** All existing fields, used for the duplicate-name warning. */
	existingFields: ScorecardField[];
	onSubmit: (input: ScorecardFieldInput) => Promise<void>;
	saving: boolean;
}

/**
 * Create/edit dialog for a scorecard field definition, including the
 * type-specific configuration (dropdown options, rating scale).
 */
export const FieldFormDialog: React.FC<FieldFormDialogProps> = ({
	open,
	onOpenChange,
	field,
	categories,
	existingFields,
	onSubmit,
	saving,
}) => {
	const { t } = useTranslation();
	const [name, setName] = useState("");
	const [description, setDescription] = useState("");
	const [fieldType, setFieldType] = useState<ScorecardFieldType>("rating");
	const [categoryId, setCategoryId] = useState<string>(NO_CATEGORY);
	const [required, setRequired] = useState(false);
	const [dropdownOptions, setDropdownOptions] = useState<string[]>([]);
	const [optionInput, setOptionInput] = useState("");
	const [rating, setRating] = useState<ScorecardRatingConfig>({ min: 1, max: 5 });

	useEffect(() => {
		if (!open) return;
		setName(field?.name ?? "");
		setDescription(field?.description ?? "");
		setFieldType(field?.field_type ?? "rating");
		setCategoryId(field?.category_id ?? NO_CATEGORY);
		setRequired(field?.required ?? false);
		setOptionInput("");
		setDropdownOptions(
			field?.field_type === "dropdown" && Array.isArray(field.options) ? field.options : [],
		);
		setRating(
			field?.field_type === "rating" && field.options && !Array.isArray(field.options)
				? field.options
				: { min: 1, max: 5 },
		);
	}, [open, field]);

	const typeLabels: Record<ScorecardFieldType, string> = {
		rating: t("admin.scorecardFields.types.rating", "Rating scale"),
		text: t("admin.scorecardFields.types.text", "Text"),
		textarea: t("admin.scorecardFields.types.textarea", "Long text"),
		number: t("admin.scorecardFields.types.number", "Number"),
		boolean: t("admin.scorecardFields.types.boolean", "Yes / No"),
		dropdown: t("admin.scorecardFields.types.dropdown", "Dropdown"),
	};

	const isDuplicateName = useMemo(() => {
		const normalized = name.trim().toLowerCase();
		if (!normalized) return false;
		return existingFields.some(
			(existing) => existing.id !== field?.id && existing.name.trim().toLowerCase() === normalized,
		);
	}, [name, existingFields, field]);

	const ratingSpan = rating.max - rating.min + 1;
	const ratingInvalid =
		fieldType === "rating" && (rating.max <= rating.min || ratingSpan > MAX_RATING_SPAN);
	const dropdownInvalid = fieldType === "dropdown" && dropdownOptions.length === 0;
	const canSubmit = !!name.trim() && !ratingInvalid && !dropdownInvalid && !saving;

	const addOption = () => {
		const option = optionInput.trim();
		if (!option || dropdownOptions.includes(option)) return;
		setDropdownOptions((previous) => [...previous, option]);
		setOptionInput("");
	};

	const handleSubmit = async () => {
		if (!canSubmit) return;
		const input: ScorecardFieldInput = {
			id: field?.id,
			name: name.trim(),
			description: description.trim() || null,
			field_type: fieldType,
			required,
			category_id: categoryId === NO_CATEGORY ? null : categoryId,
			options:
				fieldType === "dropdown" ? dropdownOptions : fieldType === "rating" ? rating : null,
		};
		await onSubmit(input);
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-lg">
				<DialogHeader>
					<DialogTitle>
						{field
							? t("admin.scorecardFields.editField", "Edit field")
							: t("admin.scorecardFields.addField", "Add field")}
					</DialogTitle>
					<DialogDescription>
						{t(
							"admin.scorecardFields.fieldFormDescription",
							"Fields appear on the Scorecard tab of every candidate profile.",
						)}
					</DialogDescription>
				</DialogHeader>
				<div className="space-y-4">
					<div className="space-y-1.5">
						<Label htmlFor="scorecard-field-name">
							{t("admin.scorecardFields.fieldName", "Name")}
						</Label>
						<Input
							id="scorecard-field-name"
							value={name}
							onChange={(event) => setName(event.target.value)}
							placeholder={t(
								"admin.scorecardFields.fieldNamePlaceholder",
								"e.g. Communication skills",
							)}
							autoFocus
						/>
						{isDuplicateName && (
							<p className="text-xs text-amber-600 dark:text-amber-500" role="alert">
								{t(
									"admin.scorecardFields.duplicateNameWarning",
									"Another field already has this name.",
								)}
							</p>
						)}
					</div>
					<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
						<div className="space-y-1.5">
							<Label>{t("admin.scorecardFields.fieldType", "Type")}</Label>
							<Select
								value={fieldType}
								onValueChange={(value) => setFieldType(value as ScorecardFieldType)}
							>
								<SelectTrigger>
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									{FIELD_TYPES.map((type) => (
										<SelectItem key={type} value={type}>
											{typeLabels[type]}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
						<div className="space-y-1.5">
							<Label>{t("admin.scorecardFields.category", "Category")}</Label>
							<Select value={categoryId} onValueChange={setCategoryId}>
								<SelectTrigger>
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value={NO_CATEGORY}>
										{t("admin.scorecardFields.noCategory", "No category")}
									</SelectItem>
									{categories.map((category) => (
										<SelectItem key={category.id} value={category.id}>
											<span className="flex items-center gap-2">
												<span
													className="h-2.5 w-2.5 shrink-0 rounded-full"
													style={{ backgroundColor: category.color ?? undefined }}
													aria-hidden="true"
												/>
												{category.name}
											</span>
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
					</div>
					<div className="space-y-1.5">
						<Label htmlFor="scorecard-field-description">
							{t("admin.scorecardFields.fieldDescription", "Description (optional)")}
						</Label>
						<Textarea
							id="scorecard-field-description"
							value={description}
							onChange={(event) => setDescription(event.target.value)}
							placeholder={t(
								"admin.scorecardFields.fieldDescriptionPlaceholder",
								"Guidance on how to evaluate this criterion",
							)}
							rows={2}
						/>
					</div>
					<div className="flex items-center justify-between rounded-sm border border-border p-3">
						<div>
							<Label htmlFor="scorecard-field-required">
								{t("admin.scorecardFields.required", "Required")}
							</Label>
							<p className="text-xs text-muted-foreground">
								{t(
									"admin.scorecardFields.requiredHint",
									"Evaluators are warned when saving with this field empty.",
								)}
							</p>
						</div>
						<Switch
							id="scorecard-field-required"
							checked={required}
							onCheckedChange={(checked) => setRequired(checked === true)}
						/>
					</div>

					{fieldType === "dropdown" && (
						<div className="space-y-2">
							<Label htmlFor="scorecard-dropdown-option">
								{t("admin.scorecardFields.dropdownOptions", "Dropdown options")}
							</Label>
							<div className="flex gap-2">
								<Input
									id="scorecard-dropdown-option"
									value={optionInput}
									onChange={(event) => setOptionInput(event.target.value)}
									onKeyDown={(event) => {
										if (event.key === "Enter") {
											event.preventDefault();
											addOption();
										}
									}}
									placeholder={t("admin.scorecardFields.optionPlaceholder", "Add an option")}
								/>
								<Button
									type="button"
									variant="outline"
									size="icon"
									onClick={addOption}
									disabled={!optionInput.trim()}
									aria-label={t("admin.scorecardFields.addOption", "Add option")}
								>
									<Plus className="h-4 w-4" />
								</Button>
							</div>
							{dropdownOptions.length > 0 ? (
								<div className="flex flex-wrap gap-1.5">
									{dropdownOptions.map((option) => (
										<Badge key={option} variant="secondary" className="gap-1 pr-1">
											{option}
											<button
												type="button"
												onClick={() =>
													setDropdownOptions((previous) =>
														previous.filter((existing) => existing !== option),
													)
												}
												aria-label={t("admin.scorecardFields.removeOption", "Remove {{option}}", {
													option,
												})}
												className="rounded-sm p-0.5 hover:bg-muted focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
											>
												<X className="h-3 w-3" />
											</button>
										</Badge>
									))}
								</div>
							) : (
								<p className="text-xs text-amber-600 dark:text-amber-500">
									{t(
										"admin.scorecardFields.validation.optionsRequired",
										"Add at least one option.",
									)}
								</p>
							)}
						</div>
					)}

					{fieldType === "rating" && (
						<div className="space-y-3">
							<Label>{t("admin.scorecardFields.ratingScale", "Rating scale")}</Label>
							<div className="grid grid-cols-2 gap-4">
								<div className="space-y-1.5">
									<Label htmlFor="scorecard-rating-min" className="text-xs text-muted-foreground">
										{t("admin.scorecardFields.ratingMin", "Minimum")}
									</Label>
									<Input
										id="scorecard-rating-min"
										type="number"
										value={rating.min}
										onChange={(event) =>
											setRating((previous) => ({
												...previous,
												min: parseInt(event.target.value, 10) || 0,
											}))
										}
									/>
								</div>
								<div className="space-y-1.5">
									<Label htmlFor="scorecard-rating-max" className="text-xs text-muted-foreground">
										{t("admin.scorecardFields.ratingMax", "Maximum")}
									</Label>
									<Input
										id="scorecard-rating-max"
										type="number"
										value={rating.max}
										onChange={(event) =>
											setRating((previous) => ({
												...previous,
												max: parseInt(event.target.value, 10) || 0,
											}))
										}
									/>
								</div>
								<div className="space-y-1.5">
									<Label
										htmlFor="scorecard-rating-min-label"
										className="text-xs text-muted-foreground"
									>
										{t("admin.scorecardFields.ratingMinLabel", "Low label (optional)")}
									</Label>
									<Input
										id="scorecard-rating-min-label"
										value={rating.minLabel ?? ""}
										onChange={(event) =>
											setRating((previous) => ({
												...previous,
												minLabel: event.target.value || undefined,
											}))
										}
										placeholder={t("admin.scorecardFields.ratingMinPlaceholder", "e.g. Poor")}
									/>
								</div>
								<div className="space-y-1.5">
									<Label
										htmlFor="scorecard-rating-max-label"
										className="text-xs text-muted-foreground"
									>
										{t("admin.scorecardFields.ratingMaxLabel", "High label (optional)")}
									</Label>
									<Input
										id="scorecard-rating-max-label"
										value={rating.maxLabel ?? ""}
										onChange={(event) =>
											setRating((previous) => ({
												...previous,
												maxLabel: event.target.value || undefined,
											}))
										}
										placeholder={t("admin.scorecardFields.ratingMaxPlaceholder", "e.g. Excellent")}
									/>
								</div>
							</div>
							{ratingInvalid && (
								<p className="text-xs text-amber-600 dark:text-amber-500" role="alert">
									{rating.max <= rating.min
										? t(
												"admin.scorecardFields.validation.ratingRange",
												"Maximum must be greater than minimum.",
											)
										: t(
												"admin.scorecardFields.validation.ratingSpan",
												"The scale can have at most {{max}} steps.",
												{ max: MAX_RATING_SPAN },
											)}
								</p>
							)}
						</div>
					)}
				</div>
				<DialogFooter>
					<Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
						{t("common.cancel", "Cancel")}
					</Button>
					<Button onClick={handleSubmit} disabled={!canSubmit}>
						{saving ? t("common.saving", "Saving...") : t("common.save", "Save")}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
};
